import { useEffect, useState } from "react";
import { api, eur, itDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Empty, ListCard } from "@/components/Crud";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function PortaleCliente() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/portal/overview").then(({ data }) => setD(data));
  }, []);

  if (!d) return <p className="overline">Caricamento…</p>;

  return (
    <div data-testid="portale-page">
      <div className="relative mb-8 overflow-hidden rounded-sm">
        <img
          src="https://images.unsplash.com/photo-1712640379137-6d2532f887a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Impianto antincendio"
          className="h-40 sm:h-52 w-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/60" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
          <p className="overline text-white/60">Portale cliente</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">{d.customer?.name}</h1>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-8">
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Impianti</p>
          <p className="font-display text-3xl font-black tracking-tighter mt-2">{d.plants.length}</p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Interventi eseguiti</p>
          <p className="font-display text-3xl font-black tracking-tighter mt-2">{d.history.length}</p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">In programma</p>
          <p className="font-display text-3xl font-black tracking-tighter mt-2">{d.scheduled.length}</p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Contratti</p>
          <p className="font-display text-3xl font-black tracking-tighter mt-2">{d.contracts.length}</p>
        </Card>
      </div>

      <Tabs defaultValue="impianti">
        <TabsList className="rounded-sm">
          <TabsTrigger value="impianti" data-testid="tab-impianti">Il mio impianto</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico">Storico</TabsTrigger>
          <TabsTrigger value="programmate" data-testid="tab-programmate">Programmate</TabsTrigger>
          <TabsTrigger value="contratti" data-testid="tab-contratti">Contratti</TabsTrigger>
        </TabsList>

        <TabsContent value="impianti" className="mt-6">
          <ListCard testid="portale-impianti">
            {d.plants.map((p) => (
              <div key={p.id} className="px-6 py-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold">{p.name}</p>
                  <Badge variant="secondary" className="rounded-sm capitalize">{p.type}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {p.address || "—"}, {p.city || "—"} · installato il {itDate(p.installed_at)}
                </p>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {(p.components || []).map((c) => (
                    <div key={c.id} className="border border-border px-3 py-2 text-xs">
                      <span className="font-semibold">{c.label}</span>
                      <span className="text-muted-foreground"> · q.tà {c.quantity} · prossima verifica {itDate(c.next_check_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {d.plants.length === 0 && <Empty text="Nessun impianto registrato." />}
          </ListCard>
        </TabsContent>

        <TabsContent value="storico" className="mt-6">
          <ListCard testid="portale-storico">
            {d.history.map((i) => (
              <div key={i.id} className="px-6 py-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm capitalize">{i.type} · {itDate(i.scheduled_date)}</p>
                  <p className="text-xs text-muted-foreground">{i.work_done || i.description || "—"}</p>
                </div>
                <span className="font-mono text-sm">{eur(i.totals?.total)}</span>
              </div>
            ))}
            {d.history.length === 0 && <Empty text="Nessun intervento eseguito." />}
          </ListCard>
        </TabsContent>

        <TabsContent value="programmate" className="mt-6">
          <ListCard testid="portale-programmate">
            {[...d.upcoming, ...d.scheduled].map((x) => (
              <div key={x.id} className="px-6 py-4 flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm">{x.plant_name || "Impianto"}</p>
                  <p className="text-xs text-muted-foreground">
                    {itDate(x.scheduled_date)} · {x.status?.replace("_", " ")}
                  </p>
                </div>
                <Badge variant="secondary" className="rounded-sm capitalize">{x.type || "manutenzione"}</Badge>
              </div>
            ))}
            {d.upcoming.length + d.scheduled.length === 0 && <Empty text="Nessuna attività programmata." />}
          </ListCard>
        </TabsContent>

        <TabsContent value="contratti" className="mt-6">
          <ListCard testid="portale-contratti">
            {d.contracts.map((c) => (
              <div key={c.id} className="px-6 py-4 flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm font-mono">{c.code}</p>
                  <p className="text-xs text-muted-foreground">
                    {itDate(c.start_date)} → {itDate(c.end_date)} · {eur(c.amount)}/anno
                  </p>
                </div>
                <Badge className="rounded-sm capitalize" variant={c.status === "attivo" ? "default" : "secondary"}>
                  {c.status}
                </Badge>
              </div>
            ))}
            {d.contracts.length === 0 && <Empty text="Nessun contratto attivo." />}
          </ListCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
